import { z } from 'zod';
import { AddressStatus } from './addresses.js';
import { IpAddress, IsoTime, Severity } from './basics.js';
import { CountryCode } from './country.js';
/*
 * The overview as a decision centre (P11.2): what needs a person now, who is
 * attacking and whether they are stopped, and how the last day went — one read,
 * `GET /v1/overview`, so the landing page answers "is anything wrong, and what do
 * I do about it?" before anyone opens another page.
 */
/** What a decision is about, which says where it is made. */
export const AttentionKind = z.enum([
    'event',
    'attacker',
    'suggestion',
    'settings_failed',
    'module_unhealthy',
    /** A server's Wazuh agent the central manager cannot reach (P11.8.5). */
    'wazuh_agent',
    /** A server with a critical or high vulnerability that has a fix (P11.8.8). */
    'vulnerability',
    'server_offline',
    'server_pending',
]);
export const AttentionItem = z.object({
    kind: AttentionKind,
    severity: Severity,
    title: z.string().max(300),
    detail: z.string().max(500).nullable(),
    /** The dashboard page the decision is made on. */
    link: z.string().max(300),
    at: IsoTime.nullable(),
});
export const MAX_ATTENTION = 30;
export const MAX_ATTACKERS = 10;
/**
 * An address worth a look in the last day: from its events, from probing — many
 * requests, most refused (4xx), which is what scanners leave and what raises no
 * event of its own — or both.
 */
export const Attacker = z.object({
    ip: IpAddress,
    events: z.number().int().nonnegative(),
    requests: z.number().int().nonnegative(),
    /** Of its requests, how many the web servers refused (4xx). */
    refused: z.number().int().nonnegative(),
    servers: z.number().int().nonnegative(),
    /** Its most frequent event category, or `probing` from traffic alone. */
    kind: z.string().max(64),
    lastSeenAt: IsoTime,
    status: AddressStatus,
    bannedOn: z.number().int().nonnegative(),
    /** Where CrowdSec placed it, if it ever raised an alert about it. */
    country: CountryCode.nullable().default(null),
    /** Whose network it is, from DB-IP. */
    network: z.string().max(200).nullable().default(null),
});
/** One hour of the last day: events by severity, and requests to the websites. */
export const OverviewHour = z.object({
    at: IsoTime,
    events: z.partialRecord(Severity, z.number().int().nonnegative()),
    requests: z.number().int().nonnegative(),
});
/** `GET /v1/overview`. */
export const OverviewResponse = z.object({
    generatedAt: IsoTime,
    fleet: z.object({
        active: z.number().int().nonnegative(),
        offline: z.number().int().nonnegative(),
        pending: z.number().int().nonnegative(),
    }),
    /** Over the last day. */
    totals: z.object({
        events: z.number().int().nonnegative(),
        highEvents: z.number().int().nonnegative(),
        requests: z.number().int().nonnegative(),
        /** Addresses CrowdSec holds back on at least one server now (community lists aside). */
        stoppedNow: z.number().int().nonnegative(),
    }),
    attention: z.array(AttentionItem).max(MAX_ATTENTION),
    /** How many of each kind there are, when the list shows only the first. */
    attentionCounts: z.partialRecord(AttentionKind, z.number().int().nonnegative()),
    attackers: z.array(Attacker).max(MAX_ATTACKERS),
    hours: z.array(OverviewHour).max(25),
});
const SEVERITY_RANK = {
    critical: 4,
    high: 3,
    medium: 2,
    low: 1,
    info: 0,
};
/** Most severe first, then newest: the order a person should decide in. */
export function byUrgency(a, b) {
    const rank = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (rank !== 0)
        return rank;
    return (b.at ?? '').localeCompare(a.at ?? '');
}
//# sourceMappingURL=overview.js.map